import {
  callApiAndHandleExpectedApiExceptions,
  ApiError,
} from "common/middleware/api.helpers";
import { ApiClientFactory } from "common/middleware/api.client-factory";
import {
  AccessTokenDto,
  RequestOtpCommand,
  ValidateOtpCommand,
} from "common/middleware/api.services";
import { mutateLoggedInUser } from "common/middleware/hooks";
import { Logger } from "common/middleware/logger";
import store, { updateAccessToken } from "common/middleware/store";

/**
 * Request an OTP code for sign-in or verification
 * @param email User email address
 * @param redirectUrl Optional URL to redirect to after successful OTP validation
 * @returns Object with success status and optional error information
 */
export const requestOtp = async (
  email: string,
  redirectUrl?: string
): Promise<{
  isSuccess: boolean;
  isUserNotFound?: boolean;
  errorCode?: string;
}> => {
  const result = await callApiAndHandleExpectedApiExceptions(
    async () => {
      const authClient = await ApiClientFactory.getAuthClient(false);
      const command = new RequestOtpCommand({
        email,
        redirectUrl,
      });
      return await authClient.requestOtp(command);
    },
    ["email_not_registered"]
  );

  if (result instanceof ApiError) {
    Logger.debug("Failed to request OTP", result.errorCode);

    return {
      isSuccess: false,
      isUserNotFound: result.errorCode === "email_not_registered",
      errorCode: result.errorCode,
    };
  }

  Logger.debug("OTP requested successfully for email", email);
  return { isSuccess: true };
};

/**
 * Validate an OTP code and receive an access token
 * @param email User email address
 * @param otpCode 6-digit OTP code
 * @returns Object with success status and token info
 */
export const validateOtp = async (
  email: string,
  otpCode: string
): Promise<{
  isSuccess: boolean;
  isInvalidCode?: boolean;
  token?: string;
  refreshToken?: string;
  isExpired?: boolean;
}> => {
  const result = await callApiAndHandleExpectedApiExceptions(
    async () => {
      const authClient = await ApiClientFactory.getAuthClient(false);
      const command = new ValidateOtpCommand({
        email,
        otpCode,
      });
      return await authClient.validateOtp(command);
    },
    ["invalid_otp_code", "otp_expired", "otp_max_retries_exceeded"]
  );

  if (result instanceof ApiError) {
    Logger.debug("OTP validation failed", result.errorCode);

    return {
      isSuccess: false,
      isInvalidCode:
        result.errorCode === "invalid_otp_code" ||
        result.errorCode === "otp_max_retries_exceeded",
      isExpired: result.errorCode === "otp_expired",
    };
  }

  if (result && result instanceof AccessTokenDto) {
    // Store the access token
    updateAccessToken(result);

    // Trigger a refresh on getLoggedInUser
    mutateLoggedInUser();

    Logger.debug("OTP validated successfully for email", email);

    return {
      isSuccess: true,
      token: result.token,
      refreshToken: result.refreshToken,
    };
  }

  Logger.debug("Unexpected response from OTP validation");
  return { isSuccess: false };
};
